import { Subtitle } from '@snapi/luxury';
import { DateUtils } from '@snapi/utils';
import { format, addMonths } from 'date-fns';
import { motion } from 'framer-motion';
import React, { useMemo, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

interface StatsByMonth {
  trimester: number;
  min: number;
  max: number;
  avg: number;
  median: number;
  deviation: number;
  upper: number;
  lower: number;
  month: number;
}

interface DiversityChartPoint {
  diversity: number;
  ageInMonths: number;
  testId: string;
  createdAt: string;
}

export interface DiversityChartProps {
  diversity: DiversityChartPoint[];
  stats: StatsByMonth[];
}

interface DiversityLabel {
  testId: string;
  createdAt: string;
  type: 'last' | 'next' | 'previous' | 'possible';
}

export const DiversityChart = ({ diversity, stats }: DiversityChartProps) => {
  const [allAges, toggleAllAges] = useState(false);

  const renderCustomAxisTick = useMemo(
    () =>
      (props: {
        x: number;
        y: number;
        index: number;
        payload: { value: number };
      }) => {
        const {
          x,
          y,
          payload: { value: ageInMonths },
        } = props;

        if (allAges && ageInMonths % 3 !== 0) {
          return <React.Fragment />;
        }

        return (
          <svg x={x - 16} y={y} width={24} height={24}>
            <text
              y={9}
              x={0}
              fontSize={9}
              fontWeight={600}
              opacity={0.6}
              color="#595959"
            >
              {ageInMonths} m
            </text>
          </svg>
        );
      },
    [allAges]
  );

  const renderTestLabel = useMemo(
    () =>
      ({
        x,
        y,
        value,
      }: {
        x?: number | string;
        y?: number | string;
        value?: string | number | DiversityLabel;
      }) => {
        if (
          !value ||
          typeof value !== 'object' ||
          !x ||
          !y ||
          typeof x !== 'number' ||
          typeof y !== 'number'
        ) {
          return null;
        }

        return (
          <>
            {value.type === 'last' && (
              <rect
                x={x + 4}
                y={y + 8}
                width={64}
                height={34}
                ry={4}
                rx={4}
                opacity={1}
                fill="#415783"
                z={0}
              />
            )}
            {value.type === 'next' && (
              <rect
                x={x + 4}
                y={y + 4}
                width={80}
                height={34}
                ry={10}
                rx={10}
                opacity={1}
                fill="#F2B3BC"
                z={0}
              />
            )}
            <text
              x={x + 10}
              y={y + 21}
              fontSize={9}
              fontWeight={600}
              fill={value.type === 'last' ? 'white' : '#595959'}
              z={10}
            >
              {value.testId}
              <tspan y={y + 35.6} x={x + 10}>
                {value.createdAt
                  ? format(new Date(value.createdAt).getTime(), 'dd MMM yyyy')
                  : ''}
              </tspan>
            </text>
          </>
        );
      },
    []
  );

  const renderTestDot = useMemo(
    () =>
      (props: {
        cx: number;
        cy: number;
        height: number;
        payload: { label: DiversityLabel };
      }) => {
        const {
          cx,
          cy,
          height,
          payload: {
            label: { type },
          },
        } = props;

        if (!cy || type === 'possible') {
          return <React.Fragment />;
        }

        const fill =
          type === 'last' ? '#415783' : type === 'next' ? '#F2B3BC' : '#A3CEF3';

        return (
          <>
            <circle
              cx={cx}
              cy={cy + (type === 'last' ? 8 : 6)}
              fill={type === 'last' ? 'white ' : fill}
              stroke={fill}
              strokeWidth={2}
              r={type === 'previous' ? 6 : 8}
            />
            <rect
              x={cx - 1}
              y={cy + 7 + (type === 'last' ? 8 : 6)}
              ry={2}
              width={2}
              height={height - cy - 2 - (type === 'last' ? 8 : 6)}
              fill={fill}
            />
          </>
        );
      },
    []
  );

  const data = useMemo(() => {
    const statsByAgeInMonths: { [key in number]: StatsByMonth } = stats.reduce(
      (acc, stat) => Object.assign(acc, { [stat.month]: stat }),
      {}
    );
    const statsByTrimester: { [key in number]: StatsByMonth } = stats.reduce(
      (acc, stat) => Object.assign(acc, { [stat.trimester]: stat }),
      {}
    );
    const diversityToAge: { [key in number]: DiversityChartPoint } =
      diversity.reduce(
        (acc, diversity) =>
          Object.assign(acc, { [diversity.ageInMonths]: diversity }),
        {}
      );

    const lastTest: DiversityChartPoint | undefined =
      diversity.length > 0 ? diversity[diversity.length - 1] : undefined;
    const lastAgeInMonths = lastTest?.ageInMonths ?? 0;
    const lastTrimester = DateUtils.trimester.fromAgeInMonths(
      lastTest?.ageInMonths ?? 0
    );

    const nextTestAgeInMonths = lastAgeInMonths + 3;
    const nextToAge = Object.assign(
      lastTest ? { [lastAgeInMonths]: lastTest } : {},
      {
        [nextTestAgeInMonths]: {
          diversity: 1.5,
          ageInMonths: statsByAgeInMonths[nextTestAgeInMonths]?.avg ?? 1,
          testId: 'Expected result',
          createdAt: addMonths(
            new Date(lastTest?.createdAt || Date.now()),
            3
          ).toISOString(),
        },
      }
    );

    if (allAges) {
      const minTrimester = Math.max(lastTrimester - 3, 0);

      const points = Array(12)
        .fill(null)
        .map((_, i) => {
          const ageInMonths: number = i * 3 + minTrimester;
          const trimester = DateUtils.trimester.fromAgeInMonths(ageInMonths);

          const pointStats =
            statsByTrimester[trimester] || stats[stats.length - 1];
          const point: DiversityChartPoint | undefined =
            diversityToAge[ageInMonths];

          const next: DiversityChartPoint | undefined = nextToAge[ageInMonths];

          if (i === 0 && !point && diversity.length !== 1) {
            return {
              diversity: pointStats.avg,
              ageInMonths,
              label: {
                testId: '',
                createdAt: '',
                type: 'possible',
              },
              avg: [pointStats.min, pointStats.max],
            };
          }

          return {
            ...point,
            next: next?.diversity,
            ageInMonths,
            avg: [pointStats.min, pointStats.max],
            label: {
              testId: point?.testId || next?.testId,
              createdAt: point?.createdAt || next?.createdAt,
              type:
                lastTest && lastTest?.testId === point?.testId
                  ? 'last'
                  : next
                  ? 'next'
                  : 'previous',
            },
          };
        });

      return points;
    }

    const minAgeInMonths = Math.max(lastAgeInMonths - 8, 0);

    return Array(12)
      .fill(null)
      .map((_, i) => {
        const ageInMonths: number = i + minAgeInMonths;
        const pointStats: any =
          statsByAgeInMonths[ageInMonths] || stats[stats.length - 1];
        const point: DiversityChartPoint | undefined =
          diversityToAge[ageInMonths];

        const next: DiversityChartPoint | undefined = nextToAge[ageInMonths];

        if (i === 0 && !point && diversity.length > 1) {
          return {
            diversity: pointStats.avg,
            ageInMonths,
            next: next?.diversity,

            label: {
              type: 'possible',
            },
            avg: [pointStats.min, pointStats.max],
          };
        }

        return {
          ...point,
          ageInMonths,
          next: next?.diversity,
          label: {
            testId: point?.testId || next?.testId,
            createdAt: point?.createdAt || next?.createdAt,
            type:
              lastTest && lastTest?.testId === point?.testId
                ? 'last'
                : next
                ? 'next'
                : 'previous',
          },
          avg: [pointStats.min, pointStats.max],
        };
      });
  }, [allAges, stats, diversity]);

  return (
    <div className="flex flex-1 flex-col rounded-[10px]  p-3 sm:p-5 bg-white h-[438px] max-w-[700px]">
      <div className="flex flex-1 overflow-hidden">
        <ResponsiveContainer height="100%" width="100%">
          <ComposedChart data={data}>
            <XAxis
              dataKey="ageInMonths"
              tick={renderCustomAxisTick}
              tickLine={false}
              axisLine={false}
            >
              <LabelList dataKey="ageInMonths" position="left" />
            </XAxis>
            <YAxis domain={[0, 6]} label={'rate'} hide />
            <CartesianGrid strokeDasharray="4 4" />
            <Area
              type="monotone"
              dataKey="avg"
              stroke="none"
              isAnimationActive={false}
              fill="#BFD4FD"
            />
            <Line
              type="natural"
              dataKey="diversity"
              stroke="none"
              // stroke="#415783"
              dot={renderTestDot}
              // strokeWidth={2}
              // connectNulls={true}
              isAnimationActive={false}
            >
              <LabelList
                dataKey="label"
                position="insideBottomRight"
                content={renderTestLabel}
              />
            </Line>
            {/* <Line
              type="natural"
              dataKey="next"
              stroke="#415783"
              strokeDasharray="4 4"
              strokeOpacity={0.3}
              dot={renderTestDot}
              strokeWidth={2}
              connectNulls
              isAnimationActive={false}
            >
              <LabelList
                dataKey="label"
                position="insideBottomRight"
                content={renderTestLabel}
              />
            </Line> */}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex">
        <div className="relative flex flex-1 h-[32px]">
          <motion.div
            animate={allAges ? { x: 88 } : { x: 0 }}
            className="absolute bg-green w-[88px] h-[32px] rounded-full"
          />
          <span
            className="flex items-center justify-center w-[88px] p-2 z-10 cursor-pointer"
            onClick={() => toggleAllAges(false)}
          >
            <Subtitle>My age</Subtitle>
          </span>
          {/* <span
            className="flex items-center justify-center w-[88px] p-2 z-10 cursor-pointer"
            onClick={() => toggleAllAges(true)}
          >
            <Subtitle>All ages</Subtitle>
          </span> */}
        </div>
        <span className="flex">
          <span className="h-3 w-3 rounded-full mx-2 flex self-center items-center bg-purple"></span>
          <small className="flex justify-center items-center">
            Normal diversity
          </small>
        </span>
      </div>
    </div>
  );
};
